import { useState } from 'react';
import { motion } from 'framer-motion';

import { useMainMachine } from '../../../utils/useMainMachine';
import { DisclosureButton } from '../basic-ui/DisclosureButton';
import { ACTIONS, STATES } from '../../../state';
import { AISuggestions } from './AISuggestions';
import { HtmlContent } from './HtmlContent';
import { VoiceAndGenerate } from './VoiceAndGenerate';
import { NarrowSelectedElement } from './NarrowSelectedElement';
import { ImageContent } from './ImageContent';

import { getVoice } from '../../../utils/utils';

import TextIcon from '@crazyegginc/hatch/dist/images/icon-menu-outline.svg?react';
import ImageIcon from '@crazyegginc/hatch/dist/images/icon-image.svg?react';

export function Controls({ showContent, showImage, setShowContentType }) {
  const { useSelector, machine } = useMainMachine();
  const selectedElement = useSelector(({ context }) => context.selectedElement);

  const showAISuggestions = useSelector((state) =>
    state.matches({ [STATES.INITIALIZED]: { [STATES.EDITOR]: STATES.AI_SUGGESTIONS } }),
  );
  const voice = useSelector(({ context }) => context.voice);
  const voices = useSelector(({ context }) => context.brandGuidelines.voices);
  const [usedVoice, setUsedVoice] = useState(getVoice(voice, voices));
  const open = useSelector(({ context }) => context.controlsOpen);

  if (showAISuggestions) {
    return <AISuggestions usedVoice={usedVoice} setUsedVoice={setUsedVoice} />;
  }

  return (
    <div className="flex flex-col" data-testid="text-controls" data-selector={selectedElement.selector}>
      <div className="h-px bg-gray-600 my-5 -mx-5" />
      <div className="flex items-center justify-between">
        <div className="w-full">
          <div className="font-semibold flex items-center">
            {showContent ? showImage ? <ImageHeader /> : <TextHeader /> : 'Element'}
          </div>
        </div>
        <div className="w-full flex justify-end">
          <DisclosureButton
            open={open}
            onClick={() => machine.send({ type: ACTIONS.TOGGLE_CONTROLS })}
            contentId="controls-content"
          />
        </div>
      </div>
      <div className="overflow-hidden">
        <motion.div
          initial={false}
          animate={
            open
              ? {
                  height: 'auto',
                  opacity: 1,
                }
              : {
                  height: 0,
                  opacity: 0,
                }
          }
          transition={{ duration: 0.4, ease: 'linear' }}
          className="mt-2.5 flex flex-col"
          id="controls-content"
          data-testid="controls-content"
        >
          {showContent ? (
            showImage ? (
              <ImageContent key={selectedElement.selector} />
            ) : (
              <HtmlContent key={selectedElement.selector} />
            )
          ) : (
            <NarrowSelectedElement key={selectedElement.selector} setShowContentType={setShowContentType} />
          )}
          <VoiceAndGenerate
            onClick={() => {
              machine.send({ type: ACTIONS.AI_REPHRASE });
              setUsedVoice(getVoice(voice, voices));
            }}
          />
        </motion.div>
      </div>
    </div>
  );
}

function ImageHeader() {
  return (
    <>
      <ImageIcon className="w-4 h-4 mr-2.5 fill-current" />
      <span>Edit Image</span>
    </>
  );
}

function TextHeader() {
  return (
    <>
      <TextIcon className="w-4 h-4 mr-2.5 fill-current" />
      <span>Edit Text</span>
    </>
  );
}
